import React, { useEffect, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { Section, SectionHeader } from "./Section";
import { DetailList, DetailRow } from "./Cards";
import { useToast } from "./Toast";
import { reportError } from "../utils/reportError";
import {
  BANK_SWIFT_FALLBACK,
  BankDetails,
  PAYMENT_CURRENCY,
  formatCzk,
  formatIban,
} from "../utils/payments";
import "./ui.css";

/**
 * PaymentInstructions – sdílené platební pokyny pro přihlášku na závod
 * i platbu členství: Section „Platební údaje" s kopírovatelnými řádky
 * (účet, částka, VS, zpráva, splatnost), QR kód, blok pro platby ze
 * zahraničí (IBAN, SWIFT) a tlačítko „Odeslat platební údaje e-mailem"
 * se spinnerem a zámkem proti dvojímu kliku. Úspěch i chyba odeslání se
 * hlásí toastem tady – volající jen dodá onSendEmail, které chyby propaguje.
 *
 * Bankovní údaje nemusí mít IBAN (klub zadal jen tuzemské číslo účtu):
 * QR kód i blok „Platba ze zahraničí" se vykreslí jen s neprázdným
 * bankDetails.iban, bez něj je místo QR krátká poznámka a platí číslo účtu
 * + variabilní symbol.
 *
 * Komponenta nic nenačítá: bankovní údaje, VS, zprávu i SPAYD řetězec
 * sestaví obrazovka z utils/payments.ts.
 */

/** Hlášky odeslání e-mailu (stejné na obou obrazovkách). */
export const EMAIL_SENT_TOAST = "E-mail s platebními údaji byl odeslán.";
export const EMAIL_FAILED_TOAST = "E-mail se nepodařilo odeslat.";

/** Velikost QR kódu na obrazovce (px); bankovní aplikace čtou i z menšího. */
const QR_SIZE = 200;

export interface PaymentInstructionsProps {
  /** Částka v Kč. */
  amount: number;
  variableSymbol: string;
  /** Zpráva pro příjemce (racePaymentMessage / membershipPaymentMessage). */
  message: string;
  bankDetails: BankDetails;
  /** Splatnost (paymentDueDate); bez ní se řádek nevykreslí. */
  dueDate?: Date;
  /**
   * SPAYD řetězec (buildPaymentQrData) – zdroj QR kódu. Jde sestavit jen
   * s IBAN; bez něj (nebo bez IBAN v bankDetails) se místo QR vykreslí
   * poznámka, že QR platba není k dispozici.
   */
  qrData?: string;
  /** Text do poznámky u platby ze zahraničí (dnes „VS Příjmení"); bez něj se řádek nevykreslí. */
  recipientNote?: string;
  /**
   * Odeslání platebních údajů e-mailem; bez něj tlačítko není. Chyby má
   * propagovat. E-mail nese QR kód, proto ho obrazovka předává jen
   * s IBAN (qrData).
   */
  onSendEmail?: () => Promise<void>;
  /** Popisek tlačítka po úspěšném odeslání (tlačítko zůstává aktivní pro opakované odeslání). */
  emailSentLabel?: string;
  /** Doplňující obsah na konci sekce (poznámky, upozornění). */
  children?: React.ReactNode;
}

export const PaymentInstructions: React.FC<PaymentInstructionsProps> = ({
  amount,
  variableSymbol,
  message,
  bankDetails,
  dueDate,
  qrData,
  recipientNote,
  onSendEmail,
  emailSentLabel = "E-mail odeslán – poslat znovu",
  children,
}) => {
  const { showToast } = useToast();

  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  // Synchronní zámek – zabere i před překreslením (double-tap).
  const sendingRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleSend = async () => {
    if (!onSendEmail || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    try {
      await onSendEmail();
      if (mountedRef.current) setSent(true);
      showToast(EMAIL_SENT_TOAST, "success");
    } catch (err) {
      reportError("PaymentInstructions.sendEmail", err);
      showToast(EMAIL_FAILED_TOAST, "error");
    } finally {
      sendingRef.current = false;
      if (mountedRef.current) setSending(false);
    }
  };

  // Údaje z loadBankDetails jsou vždy řetězce; || "" chrání před ručně
  // sestaveným objektem bez pole.
  const accountNumber = (bankDetails.accountNumber || "").trim();
  const iban = (bankDetails.iban || "").trim();
  const swift = (bankDetails.swift || "").trim() || BANK_SWIFT_FALLBACK;
  const hasIban = iban !== "";
  const ibanRow = hasIban ? (
    <DetailRow label="IBAN" value={formatIban(iban)} copyText={iban} copyable />
  ) : null;

  return (
    <Section title="Platební údaje" icon="bi-bank">
      <DetailList>
        {accountNumber ? (
          <DetailRow label="Číslo účtu" value={accountNumber} copyable />
        ) : (
          ibanRow
        )}
        <DetailRow
          label="Částka"
          value={formatCzk(amount)}
          copyText={String(amount)}
          copyable
        />
        <DetailRow label="Variabilní symbol" value={variableSymbol} copyable />
        <DetailRow label="Zpráva pro příjemce" value={message} copyable />
        {dueDate ? (
          <DetailRow
            label="Splatnost"
            value={dueDate.toLocaleDateString("cs-CZ")}
          />
        ) : null}
      </DetailList>

      {hasIban && qrData ? (
        // QR kód: bílý rámeček = klidová zóna, ať čtečka kód najde i na tmavém pozadí karty
        <div className="text-center mt-3">
          <div
            role="img"
            aria-label="QR kód pro platbu"
            style={{
              display: "inline-block",
              padding: 12,
              background: "#ffffff",
              border: "1px solid var(--lks-border, #d8dedc)",
              borderRadius: "var(--lks-radius, 10px)",
              lineHeight: 0,
            }}
          >
            <QRCode value={qrData} size={QR_SIZE} aria-hidden="true" />
          </div>
          <p className="small text-muted mt-2 mb-0">
            Naskenujte v bankovní aplikaci
          </p>
        </div>
      ) : (
        // Bez IBAN (nebo bez SPAYD řetězce) QR platba nejde sestavit.
        <p className="small text-muted text-center mt-3 mb-0">
          QR platba není k dispozici – použijte číslo účtu a variabilní symbol.
        </p>
      )}

      {hasIban ? (
        <>
          <SectionHeader
            title="Platba ze zahraničí"
            description="International payment"
            className="mt-3"
          />
          <DetailList>
            {accountNumber ? ibanRow : null}
            <DetailRow label="SWIFT / BIC" value={swift} copyable />
            {recipientNote ? (
              <DetailRow
                label="Do poznámky uveďte / Message to the recipient"
                value={recipientNote}
                copyable
              />
            ) : null}
            <DetailRow
              label="Typ poplatků / Code of charge"
              value="OUR – odesílatel"
            />
            <DetailRow label="Měna / Currency" value={PAYMENT_CURRENCY} />
          </DetailList>
        </>
      ) : null}

      {onSendEmail ? (
        <div className="d-grid mt-3">
          <button
            type="button"
            className="btn btn-outline-primary"
            disabled={sending}
            onClick={handleSend}
          >
            {sending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                Odesílám…
              </>
            ) : sent ? (
              <>
                <i className="bi bi-check2 me-2" aria-hidden="true" />
                {emailSentLabel}
              </>
            ) : (
              <>
                <i className="bi bi-envelope me-2" aria-hidden="true" />
                Odeslat platební údaje e-mailem
              </>
            )}
          </button>
        </div>
      ) : null}

      {children}
    </Section>
  );
};


export default PaymentInstructions;
